import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, AlertCircle, Building2 } from "lucide-react";

import { Layout } from "@/components/Layout";
import { PageHeader } from "@/components/PageHeader";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { createSession, createTerms } from "@/lib/api";

const schema = z.object({
  full_name: z.string().min(2, "Full name is required"),
  email: z.string().email("Enter a valid email"),
  phone_whatsapp: z.string().min(7, "Phone / WhatsApp is required"),
  aadhaar_number: z.string().regex(/^\d{12}$/, "Aadhaar must be 12 digits"),
  pan: z
    .string()
    .regex(/^[A-Z]{5}[0-9]{4}[A-Z]$/, "Enter a valid PAN (e.g. ABCDE1234F)"),
  gstin: z.string().optional().or(z.literal("")),
  business_name: z.string().min(2, "Business / company name is required"),
  pin_code: z.string().regex(/^\d{6}$/, "Pin code must be 6 digits"),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  country: z.string().min(1, "Country is required"),
  father_husband_name: z.string().min(2, "This field is required"),
  billing_address: z.string().min(5, "Billing address is required"),
  contact_number: z.string().min(7, "Contact number is required"),
  alternate_email: z
    .string()
    .email("Enter a valid email")
    .optional()
    .or(z.literal("")),
  place: z.string().min(1, "Place is required"),
  date: z.string().min(1, "Date is required"),
});

const inputClass =
  "bg-white/5 border-white/10 text-white placeholder:text-zinc-600 rounded-xl h-11 caret-[#F5C518] transition-colors duration-200 hover:border-white/25 focus-visible:ring-1 focus-visible:ring-[#F5C518] focus-visible:border-[#F5C518]";

const Field = ({ id, label, optional, error, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-sm text-zinc-300 leading-relaxed">
      {label}
      {optional && <span className="text-zinc-600 font-normal ml-1">(optional)</span>}
    </Label>
    {children}
    {error && (
      <p data-testid={`error-${id}`} className="text-xs text-red-400 flex items-center gap-1">
        <AlertCircle className="h-3 w-3" />
        {error.message}
      </p>
    )}
  </div>
);

export default function BusinessDetails() {
  const navigate = useNavigate();
  const [submitError, setSubmitError] = React.useState("");
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { country: "India", date: new Date().toISOString().slice(0, 10) },
  });

  const onSubmit = async (values) => {
    setSubmitError("");
    try {
      // 1. Create the session (status: submitted)
      const session = await createSession(values);
      const sessionId = session.session_id;

      // 2. Trigger Zoho terms creation. The backend responds with the final
      //    status once the agreement is created (or has failed).
      const terms = await createTerms(sessionId);

      // 3. Only redirect once the backend confirms status is "terms_created".
      if (terms.status === "terms_created") {
        navigate(`/terms?token=${encodeURIComponent(sessionId)}`);
        return;
      }

      // Any other outcome (e.g. terms_failed) shows an inline error, no redirect.
      setSubmitError(
        terms.terms_failure_reason ||
          "We couldn't prepare your agreement right now. Please try again."
      );
    } catch (e) {
      setSubmitError(
        "We couldn't prepare your agreement. Please check your details and try again."
      );
    }
  };

  return (
    <Layout currentStep={1}>
      <PageHeader
        step={1}
        title="Business Details"
        subtitle="Please share the details of the primary applicant as they appear on your official documents. These details help us keep your records accurate and ensure that we can prepare your invoice and other required documentation correctly."
      />

      <form
        data-testid="business-details-form"
        onSubmit={handleSubmit(onSubmit)}
        className="bg-[#121212] rounded-2xl border border-white/10 p-6 sm:p-10 shadow-2xl shadow-black/50 animate-fade-slide-up"
      >
        <div className="flex items-center gap-2 mb-8">
          <Building2 className="h-5 w-5 text-[#F5C518]" />
          <h2 className="font-heading text-lg font-semibold tracking-tight">Primary Applicant</h2>
        </div>

        <div className="flex flex-col gap-6">
          <Field id="full_name" label="Full Name (as per Aadhaar)" error={errors.full_name}>
            <Input id="full_name" data-testid="input-full_name" placeholder="Jane Doe" className={inputClass} {...register("full_name")} />
          </Field>

          <Field id="email" label="Email" error={errors.email}>
            <Input id="email" type="email" data-testid="input-email" placeholder="jane@company.com" className={inputClass} {...register("email")} />
          </Field>

          <Field id="phone_whatsapp" label="Phone / WhatsApp" error={errors.phone_whatsapp}>
            <Input id="phone_whatsapp" data-testid="input-phone_whatsapp" placeholder="+91 98765 43210" className={inputClass} {...register("phone_whatsapp")} />
          </Field>

          <Field id="aadhaar_number" label="Aadhaar Number" error={errors.aadhaar_number}>
            <Input id="aadhaar_number" inputMode="numeric" data-testid="input-aadhaar_number" placeholder="12-digit number" className={inputClass} {...register("aadhaar_number")} />
          </Field>

          <Field id="pan" label="PAN" error={errors.pan}>
            <Input id="pan" data-testid="input-pan" placeholder="ABCDE1234F" className={`${inputClass} uppercase`} {...register("pan")} onChange={(e) => setValue("pan", e.target.value.toUpperCase())} />
          </Field>

          <Field id="gstin" label="GSTIN" optional error={errors.gstin}>
            <Input id="gstin" data-testid="input-gstin" placeholder="If applicable" className={`${inputClass} uppercase`} {...register("gstin")} onChange={(e) => setValue("gstin", e.target.value.toUpperCase())} />
          </Field>

          <Field id="business_name" label="Business / Company Name" error={errors.business_name}>
            <Input id="business_name" data-testid="input-business_name" placeholder="Acme Pvt. Ltd." className={inputClass} {...register("business_name")} />
          </Field>

          <Field id="pin_code" label="Pin Code" error={errors.pin_code}>
            <Input id="pin_code" inputMode="numeric" data-testid="input-pin_code" placeholder="560001" className={inputClass} {...register("pin_code")} />
          </Field>

          <Field id="city" label="City" error={errors.city}>
            <Input id="city" data-testid="input-city" placeholder="Bengaluru" className={inputClass} {...register("city")} />
          </Field>

          <Field id="state" label="State" error={errors.state}>
            <Input id="state" data-testid="input-state" placeholder="Karnataka" className={inputClass} {...register("state")} />
          </Field>

          <Field id="country" label="Country" error={errors.country}>
            <Input id="country" data-testid="input-country" placeholder="India" className={inputClass} {...register("country")} />
          </Field>

          <Field id="father_husband_name" label="Father's / Husband's Name" error={errors.father_husband_name}>
            <Input id="father_husband_name" data-testid="input-father_husband_name" placeholder="Full name" className={inputClass} {...register("father_husband_name")} />
          </Field>

          <Field id="billing_address" label="Complete Billing Address" error={errors.billing_address}>
            <Textarea id="billing_address" data-testid="input-billing_address" rows={3} placeholder="Street, area, landmark…" className={`${inputClass.replace("h-11", "min-h-[88px] py-3")}`} {...register("billing_address")} />
          </Field>

          <Field id="contact_number" label="Contact Number" error={errors.contact_number}>
            <Input id="contact_number" data-testid="input-contact_number" placeholder="+91 98765 43210" className={inputClass} {...register("contact_number")} />
          </Field>

          <Field id="alternate_email" label="Alternate Email" optional error={errors.alternate_email}>
            <Input id="alternate_email" type="email" data-testid="input-alternate_email" placeholder="backup@company.com" className={inputClass} {...register("alternate_email")} />
          </Field>

          <Field id="place" label="Place" error={errors.place}>
            <Input id="place" data-testid="input-place" placeholder="City / location of signing" className={inputClass} {...register("place")} />
          </Field>

          <Field id="date" label="Date" error={errors.date}>
            <Input id="date" type="date" data-testid="input-date" className={`${inputClass} [color-scheme:dark]`} {...register("date")} />
          </Field>
        </div>

        {submitError && (
          <div
            data-testid="submit-error"
            className="mt-8 flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300"
          >
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{submitError}</span>
          </div>
        )}

        <button
          type="submit"
          data-testid="save-continue-btn"
          disabled={isSubmitting}
          className="mt-8 w-full h-12 rounded-full bg-[#F5C518] text-black font-semibold flex items-center justify-center gap-2 transition-all duration-200 hover:bg-[#ffd633] hover:-translate-y-0.5 hover:shadow-[0_0_15px_rgba(245,197,24,0.3)] active:scale-[0.98] active:bg-[#ffd633] touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5C518]/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a] disabled:opacity-70 disabled:hover:translate-y-0 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Preparing your agreement…
            </>
          ) : (
            <>
              Save &amp; Continue
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
      </form>
    </Layout>
  );
}
